import { useCallback, useState } from 'react';

import { WorkspaceMember } from '@futrli-sdk/people';
import { useIntl } from '@futrli/localisation';

import { getHoursMinutes } from '../../../../../utils/dates';
import { getPeriodText } from '../../../../../report-builder/components/document-library/utils';
import { openPdfForViewing } from '../../../../utils/pdf-actions';
import { ExportedPdfRowItem } from '../types';

const DATE_FORMAT = { day: '2-digit' as const, month: 'short' as const, year: 'numeric' as const };

interface UseExportedPdfRowArgs {
  item: ExportedPdfRowItem;
  members: WorkspaceMember[];
}

/**
 * Derives display values and actions for a single exported PDF row.
 * Resolves the creator member, formats dates, and provides a PDF view handler.
 *
 * @param args.item - The exported or dispatched report row item.
 * @param args.members - The list of workspace members for creator name resolution.
 * @returns Display values (name, creator, date, time, repeat, sendTo) and view state/handler.
 */
export const useExportedPdfRow = ({ item, members }: UseExportedPdfRowArgs) => {
  const intl = useIntl();
  const [isViewing, setIsViewing] = useState(false);

  const name = item.name;
  const createdBy = item.created_by;
  const created = item.created;

  const member = members.find(m => m.id === createdBy);
  const creatorName = member?.name ? `${member.name.first_name} ${member.name.last_name}` : '';

  const formattedDate = intl.formatDate(created, DATE_FORMAT);
  const formattedTime = getHoursMinutes(created);

  const repeat = item.type === 'dispatched' ? getPeriodText(item.period, item.repeating_frequency) : '-';

  const sendTo =
    item.type === 'dispatched'
      ? item.users.length > 0
        ? intl.formatMessage(
            { id: 'predict.users_count', defaultMessage: '{count} User(s)' },
            { count: item.users.length }
          )
        : '-'
      : '-';

  const creatorLabel =
    item.type === 'exported'
      ? intl.formatMessage({ id: 'predict.exported_by', defaultMessage: 'Exported by' })
      : intl.formatMessage({ id: 'predict.sent_by', defaultMessage: 'Sent by' });

  const creatorSubLabel =
    item.type === 'exported'
      ? intl.formatMessage({ id: 'predict.via_export', defaultMessage: 'via export' })
      : intl.formatMessage({ id: 'predict.via_email', defaultMessage: 'via email' });

  const handleViewPdf = useCallback(async () => {
    if (!item.download_link || !item.name) {
      return;
    }
    try {
      setIsViewing(true);
      await openPdfForViewing(item.download_link);
    } finally {
      setIsViewing(false);
    }
  }, [item.download_link, item.name]);

  return {
    name,
    creatorName,
    creatorLabel,
    creatorSubLabel,
    formattedDate,
    formattedTime,
    repeat,
    sendTo,
    isViewing,
    handleViewPdf
  };
};
